/*
 * Suppress the benign "ResizeObserver loop completed with undelivered
 * notifications" browser notice.
 *
 * Browsers emit it when a ResizeObserver callback changes layout while the
 * browser is still delivering that same batch of resize notifications. The spec
 * treats it as a notification, not an error, and it never affects production —
 * but the dev error overlay surfaces it as a fatal runtime error, masking real
 * ones.
 *
 * Deferring every callback to the next animation frame means callbacks never
 * mutate layout during the browser's delivery loop, so the notice is never
 * emitted — for every observer in the app (ours and any third-party), without
 * each one having to remember to debounce itself.
 */
export default function patchResizeObserver() {
  if (typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') {
    return
  }

  const NativeResizeObserver = window.ResizeObserver

  // Already patched (e.g. hot reload) — leave it be.
  if (NativeResizeObserver.__gcDeferred) return

  class DeferredResizeObserver extends NativeResizeObserver {
    constructor(callback) {
      let frame = null
      // Accumulate deliveries by target while a frame is pending, so multiple
      // resizes in the same frame collapse to one deferred call WITHOUT losing
      // data: a later resize of an element supersedes its earlier entry, and
      // resizes of different elements are all preserved. This keeps
      // entries[i].contentRect accurate for callbacks that read it, instead of
      // handing back a stale first batch.
      const pending = new Map()
      let lastObserver = null

      super((entries, observer) => {
        lastObserver = observer
        for (const entry of entries) pending.set(entry.target, entry)

        // Running nothing synchronously here is what keeps the browser from
        // re-looping and emitting the benign "ResizeObserver loop" notice.
        if (frame !== null) return
        frame = window.requestAnimationFrame(() => {
          frame = null
          const merged = Array.from(pending.values())
          pending.clear()
          callback(merged, lastObserver)
        })
      })

      this.__cancelFrame = () => {
        if (frame !== null) {
          window.cancelAnimationFrame(frame)
          frame = null
        }
        pending.clear()
      }
    }

    disconnect() {
      this.__cancelFrame()
      super.disconnect()
    }
  }

  DeferredResizeObserver.__gcDeferred = true
  window.ResizeObserver = DeferredResizeObserver
}
